import * as Yup from 'yup';
import { Formik } from 'formik';
import React, { Fragment } from 'react';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { getJobApplicationTaskDropdownOptions } from 'ats/utils/jobApplicationTaskTheme';
import { DateInput } from 'components/layout/form-fields/inputs/DateInput';
import { shift } from '@floating-ui/dom';
import { jobApplicationStore } from './JobApplicationStore';
import { eventManager } from 'ats/utils/EventManager';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { UsersDropdown } from 'components/layout/form-fields/inputs/UsersDropdown';
import { FormField, FormColumn } from 'components/layout/modals/components';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { formatTimeAgo } from 'shared/utils';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import {
	JobApplicationTaskOccurredOnField,
	occurredOnSchema,
	parseOccurredOn,
	toOccurredOnValue
} from './JobApplicationTaskOccurredOnField';

const ValidationSchema = Yup.object().shape({
	description: Yup.string().label('Description'),
	type: Yup.object().label('Type').required(),
	scheduledDate: Yup.string().label('Scheduled Date').nullable(),
	occurredOn: occurredOnSchema,
	assignments: Yup.array().nullable().label('Assignments')
});

export class JobApplicationTaskEdit extends ModalComponent {
	static isV2 = true;

	getInitialValues() {
		const taskOption = jobApplicationStore.getTaskByValue(this.props.task.type);

		return {
			description: this.props.task.description || '',
			type: taskOption
				? getJobApplicationTaskDropdownOptions([taskOption])[0]
				: null,
			scheduledDate: this.props.task.scheduledDate || null,
			occurredOn: parseOccurredOn(this.props.task.occurredOn),
			assignments: this.props.task?.assignments || []
		};
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				onSubmit={values => {
					this.saving();
					this.update(values);
				}}>
				{formikBag =>
					this.renderModalContainer(formikBag, 'JobApplicationTaskEdit')
				}
			</Formik>
		);
	}

	renderForm(formikBag) {
		return (
			<Fragment>
				<FormField label="Task" required error={formikBag.errors.type}>
					<Dropdown
						value={formikBag.values.type}
						onChange={item => {
							formikBag.setFieldValue('type', item);
						}}
						options={getJobApplicationTaskDropdownOptions(
							jobApplicationStore.taskOptions
						)}
						menuClassName="job-task-type-menu"
						placeholder=" "
						disabled
					/>
				</FormField>
				{jobApplicationStore.schedulableTaskTypes.includes(
					formikBag.values.type?.value
				) ? (
					<FormField
						label="Scheduled Date"
						error={formikBag.errors.scheduledDate}>
						<DateInput
							value={
								formikBag.values.scheduledDate
									? new Date(formikBag.values.scheduledDate)
									: null
							}
							popperPlacement="top-start"
							popperModifiers={[
								shift({
									padding: 5,
									crossAxis: true
								})
							]}
							onBlur={() => formikBag.setFieldTouched('scheduledDate')}
							onChange={newDate => {
								formikBag.setFieldValue('scheduledDate', newDate);
							}}
							isDateTime
						/>
					</FormField>
				) : null}
				<JobApplicationTaskOccurredOnField formikBag={formikBag} />
				<FormField label="Assigned to" error={formikBag.errors.assignments}>
					<UsersDropdown
						value={formikBag.values.assignments}
						isMulti
						placeholder=" "
						role={NON_CANDIDATE_USER_ROLES}
						onValueChange={values => {
							formikBag.setFieldValue('assignments', values);
						}}
						showAvatar
					/>
				</FormField>
				<FormColumn>
					<label className="form-field-label-outside">Description</label>
					<FormField error={formikBag.errors.description}>
						<FormikFormField
							className="has-text-left"
							field="description"
							type="textarea"
							placeholder="Description"
							maxlength={2500}
						/>
					</FormField>
				</FormColumn>
				<div className="task-edit-audit-row">
					<span className="task-edit-audit-item">
						<span className="task-edit-audit-label">Created</span>
						<UserProfileAvatar
							imageUrl={
								this.props.task?.createdByUser?.imageUrl ??
								this.props.task?.createdBy?.imageUrl
							}
							name={
								this.props.task?.createdByUser?.name ??
								this.props.task?.createdBy?.name
							}
							tooltipPosition="top"
						/>
						<span className="task-edit-audit-date">
							{this.props.task?.createdAt
								? formatTimeAgo(new Date(this.props.task.createdAt), {
										moreThanOneDay: true
									})
								: '—'}
						</span>
					</span>
					<span className="task-edit-audit-item">
						<span className="task-edit-audit-label">Updated</span>
						<UserProfileAvatar
							imageUrl={this.props.task?.updatedBy?.imageUrl}
							name={this.props.task?.updatedBy?.name}
							tooltipPosition="top"
						/>
						<span className="task-edit-audit-date">
							{this.props.task?.updatedAt
								? formatTimeAgo(new Date(this.props.task.updatedAt), {
										moreThanOneDay: true
									})
								: '—'}
						</span>
					</span>
					{this.props.task?.status !== 'pending' && (
						<span className="task-edit-audit-item">
							<span className="task-edit-audit-label">Closed</span>
							<UserProfileAvatar
								imageUrl={
									this.props.task?.closedByUser?.imageUrl ??
									this.props.task?.closedBy?.imageUrl
								}
								name={
									this.props.task?.closedByUser?.name ??
									this.props.task?.closedBy?.name
								}
								tooltipPosition="top"
							/>
							<span className="task-edit-audit-date">
								{this.props.task?.closedOn
									? formatTimeAgo(new Date(this.props.task.closedOn), {
											moreThanOneDay: true
										})
									: '—'}
							</span>
						</span>
					)}
				</div>
			</Fragment>
		);
	}

	async update(values) {
		jobApplicationStore
			.updateTask(this.props.jobApplicationId, this.props.task.id, {
				jobApplicationTask: {
					description: values.description,
					scheduledDate: jobApplicationStore.schedulableTaskTypes.includes(
						values.type?.value
					)
						? values.scheduledDate
						: null,
					...(jobApplicationStore.isMilestoneTaskType(values.type?.value) && {
						occurredOn: toOccurredOnValue(values.occurredOn)
					}),
					assignments: values.assignments?.map(assignment => assignment?.id)
				}
			})
			.then(result => {
				if (result.status === 'success') {
					eventManager.emit('taskChanged');
				}
				this.resultNotification(result, 'Task', 'Update');
			});
	}
}
